<script setup lang="ts">
import noImage from "@/assets/imgs/no-image.jpg";
import { Hotel } from "@/modules/hotels/interfaces/hotel";
import { QuestionMarkCircleIcon, StarIcon } from "@heroicons/vue/20/solid";
import { computed, provide, ref } from "vue";
import { displayedCategory } from "../composables/useHotelCategory";

interface Props {
  displayHotels: boolean;
  selectedHotel?: Hotel;
  reservationNights: number;
  closeInfoWindow: (id: string | undefined) => void;
  openDisclaimerModal: (disclaimer: string) => void;
}

interface Emits {
  (event: "pushToDetails", hotel: Hotel): boolean;
}

const props = defineProps<Props>();
const emits = defineEmits<Emits>();

// En Hotelbeds, la categoría oficial del proveedor; si no, el rating del catálogo.
const category = computed(() =>
  props.selectedHotel ? displayedCategory(props.selectedHotel) : null,
);

const stars = computed(() => category.value?.stars ?? 0);

const hotelImage = computed(() => {
  if (!props.selectedHotel) return;

  return props.selectedHotel.image || noImage;
});

const nightFareDisclaimer =
  "El costo por noche es un aproximado; puede variar respecto al desglose de costos al momento de realizar la reserva.";

const currency = ref(false);

const changeCurrency = () => {
  currency.value = !currency.value;
};

provide("currency", currency);

const currencyFare = () => {
  if (currency.value) {
    return `${props.selectedHotel?.fare} ${props.selectedHotel?.currency}`;
  } else {
    return `${props.selectedHotel?.fareApproximate} ${props.selectedHotel?.requestedCurrency}`;
  }
};

const currencyToggleLabel = computed(() =>
  currency.value
    ? `Ver precio en ${props.selectedHotel?.requestedCurrency} (Aprox)`
    : `Ver precio en ${props.selectedHotel?.currency}`,
);

// Sin `opacity-*`: crea su propia capa y los íconos quedaban pintados encima del botón.
// h-6 w-6 (24px, el mínimo táctil de WCAG 2.5.8); el proyecto usa Tailwind 3.3, que no
// tiene `size-*`.
const mutedTextClass = computed(() =>
  props.selectedHotel?.agreement ? "text-white/80" : "text-gray-500",
);
const iconButtonClass = computed(() => [
  "grid h-6 w-6 shrink-0 place-items-center rounded-full transition-colors",
  props.selectedHotel?.agreement
    ? "text-white/80 hover:bg-white/15 hover:text-white"
    : "text-gray-500 hover:bg-gray-100 hover:text-gray-900",
]);

const currencyDailyFare = () => {
  if (currency.value) {
    return `$${props.selectedHotel?.dailyRate} ${props.selectedHotel?.currency}`;
  } else {
    return `$${props.selectedHotel?.dailyRateApproximate} ${props.selectedHotel?.requestedCurrency}`;
  }
};
</script>

<template>
  <!--
    Tarjeta compacta del hotel elegido en el mapa: lo justo para decidir (nombre,
    estrellas, precio y el botón) sin tapar el mapa ni los marcadores de alrededor.
    z-[3]: por encima de los controles de Mapbox (z-index 2), que en angosto le
    tapaban la esquina de «Ver tarifa».
  -->
  <Transition name="hotel-info">
    <div
      v-if="selectedHotel && selectedHotel.id"
      :class="[
        'absolute left-1/2 z-[3] flex w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 items-stretch overflow-hidden rounded-xl bg-white text-gray-900 shadow-lg ring-1 ring-black/10',
        selectedHotel.agreement && '!bg-blue-ef-400 !text-white ring-blue-ef-100',
        displayHotels ? 'bottom-4' : 'bottom-20 md:bottom-6',
      ]"
    >
      <!-- La foto llena el alto que da el texto: una foto vertical ya no estira la tarjeta. -->
      <div class="relative w-20 shrink-0 sm:w-24">
        <img
          class="absolute inset-0 h-full w-full object-cover"
          :src="hotelImage"
          alt=""
        />
      </div>

      <div class="flex min-w-0 flex-1 flex-col gap-1 px-3 py-2">
        <div class="flex items-start gap-1">
          <h2
            class="line-clamp-2 min-w-0 flex-1 text-sm font-semibold leading-5"
            :title="selectedHotel.name"
          >
            {{ selectedHotel.name }}
          </h2>
          <button
            type="button"
            :class="[iconButtonClass, '-mr-1.5 -mt-0.5']"
            aria-label="Cerrar"
            @click="() => closeInfoWindow(selectedHotel?.id)"
          >
            <span class="material-symbols-outlined text-[18px] leading-none" aria-hidden="true">close</span>
          </button>
        </div>

        <p v-if="category?.text" class="text-[11px] font-semibold text-gray-700" :aria-label="category.label">
          {{ category.text }}
        </p>
        <div v-else class="flex items-center gap-0.5" role="img" :aria-label="stars ? category?.label : 'Sin rating'">
          <StarIcon
            v-for="star in stars"
            :key="star"
            class="h-3.5 w-3.5"
            :class="selectedHotel.agreement ? 'text-white' : 'text-amber-500'"
            aria-hidden="true"
          />
        </div>

        <!--
          Precio y «Ver tarifa» en un renglón que envuelve: si no caben (360px, otra
          moneda, precio largo), el botón baja a su propio renglón en vez de quedar
          tapado por el precio.
        -->
        <div class="mt-auto flex flex-wrap items-end gap-x-2 gap-y-1.5">
          <div class="min-w-0 flex-1 basis-40">
            <div class="flex flex-wrap items-center gap-x-1 text-sm font-bold">
              <span>{{ currencyDailyFare() }}</span>
              <span class="text-[11px] font-normal" :class="mutedTextClass">/noche</span>
              <span class="flex items-center gap-1">
                <button
                  type="button"
                  :class="iconButtonClass"
                  aria-label="Sobre el precio por noche"
                  @click="openDisclaimerModal(nightFareDisclaimer)"
                >
                  <QuestionMarkCircleIcon class="h-4 w-4" aria-hidden="true" />
                </button>
                <button
                  v-if="selectedHotel.currency !== selectedHotel.requestedCurrency"
                  type="button"
                  :class="iconButtonClass"
                  :aria-label="currencyToggleLabel"
                  v-tooltip="currencyToggleLabel"
                  @click="changeCurrency"
                >
                  <span class="material-symbols-outlined text-[16px] leading-none" aria-hidden="true">currency_exchange</span>
                </button>
              </span>
            </div>
            <!-- Como la lista en modo mapa (HotelEntry): el total trae impuestos y el precio por noche no. -->
            <p class="text-[11px] leading-4" :class="mutedTextClass">
              Total ${{ currencyFare() }} ·
              {{ reservationNights }} {{ reservationNights === 1 ? "noche" : "noches" }} · Imp. inc.
            </p>
          </div>

          <!-- pushToDetails abre en pestaña nueva: el mapa y la búsqueda se conservan. -->
          <button
            type="button"
            class="ml-auto shrink-0 whitespace-nowrap rounded-lg bg-blue-ef-500 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-blue-ef-400"
            :class="selectedHotel.agreement && '!bg-white !text-blue-ef-500'"
            @click="emits('pushToDetails', selectedHotel)"
          >
            Ver tarifa
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style lang="css" scoped>
.hotel-info-enter-active,
.hotel-info-leave-active {
  transition: opacity 250ms ease-in-out;
}
.hotel-info-enter-from,
.hotel-info-leave-to {
  opacity: 0;
}
</style>
